import { messageErreur } from '@imp/shared/message-erreur';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowLeft, Wand2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ChoixChantier } from '@/modules/chantiers/ChoixChantier';
import { listChantiers } from '@/modules/chantiers/api';
import { ChoixClient } from '@/modules/clients/ChoixClient';
import { listContacts } from '@/modules/clients/api';
import {
  type Answers,
  type AnswerValue,
  createDevisFromQcm,
  getQuestionnaire,
  type QcmQuestion,
} from '@/modules/qcm/api';

const NUMERIC_TYPES = ['nombre', 'surface_m2', 'lineaire_ml', 'volume_m3'];

function isEmpty(v: AnswerValue | undefined): boolean {
  return v === null || v === undefined || v === '';
}

export function QcmFillPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const qcmQ = useQuery({
    queryKey: ['questionnaire', id],
    queryFn: () => getQuestionnaire(id ?? ''),
    enabled: Boolean(id),
  });
  const contactsQ = useQuery({ queryKey: ['contacts'], queryFn: listContacts });
  const chantiersQ = useQuery({ queryKey: ['chantiers'], queryFn: listChantiers });

  const [contactId, setContactId] = useState('');
  const [chantierId, setChantierId] = useState('');
  const [answers, setAnswers] = useState<Answers>({});
  const setAnswer = (code: string, value: AnswerValue) => {
    setAnswers((a) => ({ ...a, [code]: value }));
  };

  const modele = qcmQ.data;
  const allQuestions = (modele?.questionnaire_sections ?? []).flatMap((s) => s.questions);
  const missing = allQuestions.filter((q) => q.obligatoire && isEmpty(answers[q.code]));
  const canSubmit =
    contactId !== '' && chantierId !== '' && missing.length === 0 && allQuestions.length > 0;

  const mutation = useMutation({
    mutationFn: () => {
      if (!modele) throw new Error('Questionnaire non chargé.');
      return createDevisFromQcm(modele, answers, contactId, chantierId);
    },
    onSuccess: (devisId) => {
      navigate(`/devis/${devisId}`);
    },
  });

  const strVal = (code: string): string => {
    const v = answers[code];
    return v === null || v === undefined ? '' : String(v);
  };

  function renderInput(q: QcmQuestion) {
    if (q.type === 'booleen') {
      const v = answers[q.code];
      return (
        <Select
          id={q.code}
          value={v === true ? 'oui' : v === false ? 'non' : ''}
          onChange={(e) => {
            const val = e.target.value;
            setAnswer(q.code, val === '' ? null : val === 'oui');
          }}
        >
          <option value="">—</option>
          <option value="oui">Oui</option>
          <option value="non">Non</option>
        </Select>
      );
    }
    if (q.type === 'choix_unique') {
      return (
        <Select
          id={q.code}
          value={strVal(q.code)}
          onChange={(e) => {
            setAnswer(q.code, e.target.value === '' ? null : e.target.value);
          }}
        >
          <option value="">— Choisir —</option>
          {q.question_options.map((o) => (
            <option key={o.id} value={o.code}>
              {o.libelle}
            </option>
          ))}
        </Select>
      );
    }
    if (q.type === 'texte_long') {
      return (
        <Textarea
          id={q.code}
          value={strVal(q.code)}
          onChange={(e) => {
            setAnswer(q.code, e.target.value);
          }}
        />
      );
    }
    if (q.type === 'date') {
      return (
        <Input
          id={q.code}
          type="date"
          value={strVal(q.code)}
          onChange={(e) => {
            setAnswer(q.code, e.target.value);
          }}
        />
      );
    }
    if (NUMERIC_TYPES.includes(q.type)) {
      return (
        <div className="flex items-center gap-2">
          <Input
            id={q.code}
            inputMode="decimal"
            placeholder="0"
            value={strVal(q.code)}
            onChange={(e) => {
              setAnswer(q.code, e.target.value);
            }}
          />
          {q.unite ? <span className="text-sm text-muted-foreground">{q.unite}</span> : null}
        </div>
      );
    }
    // texte + types non gérés en MVP
    return (
      <Input
        id={q.code}
        value={strVal(q.code)}
        onChange={(e) => {
          setAnswer(q.code, e.target.value);
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="icon">
          <Link to="/devis/qcm" aria-label="Retour">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <h1 className="text-2xl font-bold tracking-tight">{modele?.nom ?? 'Devis guidé'}</h1>
      </div>

      {qcmQ.isLoading ? (
        <p className="text-muted-foreground">Chargement…</p>
      ) : qcmQ.isError ? (
        <p className="text-destructive">Erreur : {messageErreur(qcmQ.error)}</p>
      ) : !modele ? null : (
        <form
          className="space-y-6"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) mutation.mutate();
          }}
        >
          {mutation.isError ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-destructive">
              Échec : {messageErreur(mutation.error)}
            </div>
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle>Client</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1.5">
                <Label htmlFor="contact_id">Client *</Label>
                <ChoixClient
                  id="contact_id"
                  clients={contactsQ.data ?? []}
                  chargement={contactsQ.isLoading}
                  value={contactId}
                  onChange={setContactId}
                />
              </div>
              <div className="mt-4 space-y-1.5">
                <Label htmlFor="chantier_id">Chantier *</Label>
                <ChoixChantier
                  id="chantier_id"
                  chantiers={chantiersQ.data ?? []}
                  clients={contactsQ.data ?? []}
                  chargement={chantiersQ.isLoading}
                  value={chantierId}
                  onChange={setChantierId}
                />
                <p className="text-muted-foreground text-xs">
                  Le devis produit sera rattaché à ce chantier.
                </p>
              </div>
            </CardContent>
          </Card>

          {modele.questionnaire_sections.map((s) => (
            <Card key={s.id}>
              <CardHeader>
                <CardTitle>{s.titre}</CardTitle>
                {s.description ? (
                  <p className="text-sm text-muted-foreground">{s.description}</p>
                ) : null}
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2">
                {s.questions.map((q) => (
                  <div key={q.id} className="space-y-1.5">
                    <Label htmlFor={q.code}>
                      {q.libelle}
                      {q.obligatoire ? ' *' : ''}
                    </Label>
                    {renderInput(q)}
                    {q.aide ? <p className="text-xs text-muted-foreground">{q.aide}</p> : null}
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}

          <div className="flex items-center gap-3">
            <Button type="submit" disabled={!canSubmit || mutation.isPending}>
              <Wand2 className="h-4 w-4" />
              {mutation.isPending ? 'Génération…' : 'Générer le devis'}
            </Button>
            <Button asChild variant="outline" type="button">
              <Link to="/devis/qcm">Annuler</Link>
            </Button>
            {contactId === '' ? (
              <span className="text-sm text-muted-foreground">Sélectionnez un client.</span>
            ) : missing.length > 0 ? (
              <span className="text-sm text-muted-foreground">
                {missing.length} question(s) obligatoire(s) à compléter.
              </span>
            ) : null}
          </div>
        </form>
      )}
    </div>
  );
}
